import {
  AlertTriangle,
  FileText,
  Package,
  RefreshCw,
  ShoppingCart,
  TrendingDown,
} from "lucide-react";
import { useEffect, useState } from "react";
import { resolveValidStoreId } from "@medicorepay/shared";
import { showToast } from "../../lib/toast";
import {
  reorderService,
  type ReorderPriority,
  type ReorderSuggestion,
} from "../../services/reorder.service";
import { useAuthStore } from "../../stores/authStore";
import {
  supplierService,
  type Supplier,
} from "../../services/supplier.service";

const PRIORITY_STYLES: Record<
  ReorderPriority,
  { bg: string; text: string; label: string }
> = {
  CRITICAL: { bg: "bg-red-100", text: "text-red-700", label: "Critical" },
  HIGH: { bg: "bg-orange-100", text: "text-orange-700", label: "High" },
  MEDIUM: { bg: "bg-yellow-100", text: "text-yellow-700", label: "Medium" },
  LOW: { bg: "bg-blue-100", text: "text-blue-700", label: "Low" },
};

export default function SmartReorderPage() {
  const [suggestions, setSuggestions] = useState<ReorderSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterPriority, setFilterPriority] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<
    Map<string, { qty: number; supplierId: string }>
  >(new Map());
  const [generating, setGenerating] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const selectedStoreId = useAuthStore((state) =>
    resolveValidStoreId(
      state.selectedStoreId,
      state.user?.storeId,
      state.user?.stores,
    ),
  );

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      const data = await reorderService.getSuggestions({
        priority: (filterPriority as ReorderPriority) || undefined,
        storeId: selectedStoreId ?? undefined,
      });
      setSuggestions(data);
    } catch {
      showToast.error("Failed to load reorder suggestions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSuggestions();
  }, [filterPriority, selectedStoreId]);

  useEffect(() => {
    supplierService
      .getSuppliers({ page: 1, limit: 100, isActive: true })
      .then((response) => setSuppliers(response.data))
      .catch(() => setSuppliers([]));
  }, []);

  const toggleSelection = (s: ReorderSuggestion) => {
    setSelectedItems((prev) => {
      const next = new Map(prev);
      if (next.has(s.productId)) {
        next.delete(s.productId);
      } else {
        next.set(s.productId, {
          qty: Math.max(1, s.suggestedQty),
          supplierId: s.preferredSupplier?.id ?? "",
        });
      }
      return next;
    });
  };

  const selectAll = () => {
    if (selectedItems.size === suggestions.length) {
      setSelectedItems(new Map());
    } else {
      const next = new Map<string, { qty: number; supplierId: string }>();
      suggestions.forEach((s) =>
        next.set(s.productId, {
          qty: Math.max(1, s.suggestedQty),
          supplierId: s.preferredSupplier?.id ?? "",
        }),
      );
      setSelectedItems(next);
    }
  };

  const handleGeneratePO = async () => {
    if (selectedItems.size === 0) return;
    if (!selectedStoreId) {
      showToast.error("Select a valid store before generating purchase orders");
      return;
    }
    const missingSupplierCount = [...selectedItems.values()].filter(
      (item) => !item.supplierId,
    ).length;
    if (missingSupplierCount > 0) {
      showToast.error(
        `Select a supplier for ${missingSupplierCount} selected item(s)`,
      );
      return;
    }

    // Group by supplier
    const bySupplier = new Map<
      string,
      Array<{ productId: string; qty: number }>
    >();
    selectedItems.forEach((val, productId) => {
      const key = val.supplierId || "unknown";
      if (!bySupplier.has(key)) bySupplier.set(key, []);
      bySupplier.get(key)!.push({ productId, qty: val.qty });
    });

    setGenerating(true);
    try {
      let created = 0;
      for (const [supplierId, items] of bySupplier) {
        if (!supplierId || supplierId === "unknown") continue;
        await reorderService.generatePurchaseOrder({
          suggestions: items,
          supplierId,
          storeId: selectedStoreId,
        });
        created++;
      }
      if (created === 0) {
        throw new Error("No purchase orders were created");
      }
      showToast.success(`${created} purchase order(s) created as drafts`);
      loadSuggestions();
      setSelectedItems(new Map());
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to generate purchase orders",
      );
    } finally {
      setGenerating(false);
    }
  };

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(v);

  const totalEstimatedCost = suggestions
    .filter((s) => selectedItems.has(s.productId))
    .reduce((sum, s) => {
      const sel = selectedItems.get(s.productId)!;
      return sum + sel.qty * s.lastPurchasePrice;
    }, 0);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Smart Reorder Suggestions
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Auto-generated based on sales velocity, current stock, and min stock
            levels
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="">All Priorities</option>
            <option value="CRITICAL">Critical</option>
            <option value="HIGH">High</option>
            <option value="MEDIUM">Medium</option>
            <option value="LOW">Low</option>
          </select>
          <button
            onClick={loadSuggestions}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <SummaryCard
          label="Total Suggestions"
          value={suggestions.length.toString()}
          icon={<Package className="h-5 w-5 text-teal-600" />}
          bg="bg-teal-50"
        />
        <SummaryCard
          label="Critical Items"
          value={suggestions
            .filter((s) => s.priority === "CRITICAL")
            .length.toString()}
          icon={<AlertTriangle className="h-5 w-5 text-red-600" />}
          bg="bg-red-50"
        />
        <SummaryCard
          label="Selected Items"
          value={selectedItems.size.toString()}
          icon={<ShoppingCart className="h-5 w-5 text-blue-600" />}
          bg="bg-blue-50"
        />
        <SummaryCard
          label="Estimated Cost"
          value={formatCurrency(totalEstimatedCost)}
          icon={<TrendingDown className="h-5 w-5 text-purple-600" />}
          bg="bg-purple-50"
        />
      </div>

      {/* Action Bar */}
      {selectedItems.size > 0 && (
        <div className="flex items-center gap-3 bg-teal-50 border border-teal-200 rounded-xl p-4">
          <FileText className="h-5 w-5 text-teal-600" />
          <span className="text-sm font-medium text-teal-800">
            {selectedItems.size} item(s) selected — Estimated:{" "}
            {formatCurrency(totalEstimatedCost)}
          </span>
          <button
            onClick={handleGeneratePO}
            disabled={generating}
            className="ml-auto flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
          >
            <ShoppingCart className="h-4 w-4" />
            {generating ? "Generating..." : "Generate Purchase Order(s)"}
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-400">
            Analyzing sales velocity and stock levels...
          </div>
        ) : suggestions.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p className="font-medium">No reorder suggestions</p>
            <p className="text-sm mt-1">
              All products are above minimum stock levels
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="py-3 px-4 text-left">
                    <input
                      type="checkbox"
                      checked={selectedItems.size === suggestions.length}
                      onChange={selectAll}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">
                    Product
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Priority
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Current Stock
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Min Level
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Avg Daily Sales
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Days Left
                  </th>
                  <th className="py-3 px-4 text-center font-medium text-gray-600">
                    Suggested Qty
                  </th>
                  <th className="py-3 px-4 text-left font-medium text-gray-600">
                    Supplier
                  </th>
                  <th className="py-3 px-4 text-right font-medium text-gray-600">
                    Est. Cost
                  </th>
                </tr>
              </thead>
              <tbody>
                {suggestions.map((s) => {
                  const style = PRIORITY_STYLES[s.priority];
                  const isSelected = selectedItems.has(s.productId);

                  return (
                    <tr
                      key={s.productId}
                      className={`border-b border-gray-50 hover:bg-gray-50 ${
                        isSelected ? "bg-teal-50" : ""
                      }`}
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelection(s)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">
                          {s.productName}
                        </div>
                        {s.sku && (
                          <div className="text-xs text-gray-400">{s.sku}</div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`${style.bg} ${style.text} text-xs font-medium px-2 py-1 rounded-full`}
                        >
                          {style.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center font-medium">
                        {s.currentStock}
                      </td>
                      <td className="py-3 px-4 text-center text-gray-500">
                        {s.minStockLevel}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {s.avgDailySales}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span
                          className={`font-medium ${
                            s.daysOfStockLeft <= 3
                              ? "text-red-600"
                              : s.daysOfStockLeft <= 7
                                ? "text-orange-600"
                                : "text-gray-900"
                          }`}
                        >
                          {s.daysOfStockLeft === 999
                            ? "∞"
                            : `${s.daysOfStockLeft}d`}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {isSelected ? (
                          <input
                            type="number"
                            min={1}
                            value={
                              selectedItems.get(s.productId)?.qty ??
                              s.suggestedQty
                            }
                            onChange={(e) => {
                              const qty = Math.max(1, Number(e.target.value));
                              setSelectedItems((prev) => {
                                const next = new Map(prev);
                                next.set(s.productId, {
                                  ...next.get(s.productId)!,
                                  qty,
                                });
                                return next;
                              });
                            }}
                            className="w-20 text-center border border-gray-200 rounded-lg py-1 text-sm"
                          />
                        ) : (
                          <span className="font-medium">{s.suggestedQty}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-gray-600 text-sm">
                        {isSelected ? (
                          <select
                            aria-label={`Supplier for ${s.productName}`}
                            value={
                              selectedItems.get(s.productId)?.supplierId ?? ""
                            }
                            onChange={(event) => {
                              setSelectedItems((previous) => {
                                const next = new Map(previous);
                                next.set(s.productId, {
                                  ...next.get(s.productId)!,
                                  supplierId: event.target.value,
                                });
                                return next;
                              });
                            }}
                            className="max-w-44 rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm"
                          >
                            <option value="">Select supplier</option>
                            {suppliers.map((supplier) => (
                              <option key={supplier.id} value={supplier.id}>
                                {supplier.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          (s.preferredSupplier?.name ?? "Select after choosing")
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {formatCurrency(s.estimatedCost)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  icon,
  bg,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  bg: string;
}) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            {label}
          </p>
          <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`${bg} p-3 rounded-xl`}>{icon}</div>
      </div>
    </div>
  );
}
